import React from 'react';
import { Card, CardContent, Typography, Divider } from '@material-ui/core';

// Ícones por contexto — importados sob demanda
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import EditIcon from '@material-ui/icons/Edit';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import SettingsIcon from '@material-ui/icons/Settings';
import ReceiptIcon from '@material-ui/icons/Receipt';
import PaymentIcon from '@material-ui/icons/Payment';
import HistoryIcon from '@material-ui/icons/History';
import SearchIcon from '@material-ui/icons/Search';
import BuildIcon from '@material-ui/icons/Build';
import ListAltIcon from '@material-ui/icons/ListAlt';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import AssignmentIcon from '@material-ui/icons/Assignment';

/**
 * Contextos pré-definidos que determinam cor do borderLeft + ícone padrão.
 * Usar sempre que houver um card no projeto admin Financeiro.
 */
const CONTEXT = {
    info:     { color: '#1976d2', icon: InfoOutlinedIcon },    // azul — informações gerais / leitura
    edit:     { color: '#ff9800', icon: EditIcon },             // laranja — formulário / editável
    success:  { color: '#4caf50', icon: CheckCircleIcon },      // verde — OK / confirmação
    warning:  { color: '#f57c00', icon: WarningIcon },          // laranja escuro — atenção
    danger:   { color: '#d32f2f', icon: ErrorOutlineIcon },     // vermelho — destrutivo / zona de perigo
    settings: { color: '#546e7a', icon: SettingsIcon },         // cinza-azulado — configuração
    receipt:  { color: '#8e24aa', icon: ReceiptIcon },          // roxo — faturas / invoices
    payment:  { color: '#00897b', icon: PaymentIcon },          // teal — pagamentos
    history:  { color: '#757575', icon: HistoryIcon },          // cinza — histórico
    search:   { color: '#1976d2', icon: SearchIcon },           // azul — busca / filtros
    provisioning: { color: '#3949ab', icon: BuildIcon },        // índigo — provisionamento
    list:     { color: '#455a64', icon: ListAltIcon },          // cinza-azulado — listagens
    tree:     { color: '#5e35b1', icon: AccountTreeIcon },      // violeta — hierarquia / planos
    assignment: { color: '#00838f', icon: AssignmentIcon },     // cyan — tarefas / formulários
};

/**
 * Card padrão do admin Financeiro Mexx — visual profissional unificado.
 *
 * Props:
 *  - context: chave de CONTEXT (info|edit|success|warning|danger|settings|receipt|payment|history|search|provisioning|list|tree|assignment)
 *  - title: string
 *  - icon: ícone customizado (opcional — sobrescreve o default do contexto)
 *  - color: cor do borderLeft customizada (opcional — sobrescreve o default do contexto)
 *  - subtitle: string opcional (cinza abaixo do título)
 *  - actions: ReactNode opcional renderizado no canto direito do header (botões, toggles)
 *  - divider: bool (default true) — mostra divider abaixo do header
 *  - noPadding: bool — remove o padding interno do CardContent (útil pra embutir tabelas)
 *  - style, contentStyle: override opcional
 *  - children: conteúdo
 */
function SectionCard({
    context = 'info',
    title,
    icon,
    color,
    subtitle,
    actions,
    divider = true,
    noPadding = false,
    style,
    contentStyle,
    children,
}) {
    const ctx = CONTEXT[context] || CONTEXT.info;
    const borderColor = color || ctx.color;
    const IconComponent = icon || ctx.icon;

    return (
        <Card
            style={{
                border: '1px solid #e0e0e0',
                borderLeft: `4px solid ${borderColor}`,
                borderRadius: 8,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                ...style,
            }}
        >
            <CardContent style={{ ...(noPadding ? { padding: 0 } : {}), ...contentStyle }}>
                {(title || actions) && (
                    <>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            padding: noPadding ? '16px 20px 12px' : 0,
                            marginBottom: noPadding ? 0 : 12,
                        }}>
                            {title && (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                                    {IconComponent && <IconComponent style={{ color: borderColor, marginTop: 2 }} />}
                                    <div>
                                        <Typography
                                            variant="h6"
                                            style={{
                                                fontWeight: 600,
                                                fontSize: 16,
                                                color: '#263238',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {title}
                                        </Typography>
                                        {subtitle && (
                                            <Typography variant="body2" style={{ color: '#757575', marginTop: 2, fontSize: 12 }}>
                                                {subtitle}
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                            )}
                            {actions && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                    {actions}
                                </div>
                            )}
                        </div>
                        {divider && <Divider style={{ marginBottom: noPadding ? 0 : 16 }} />}
                    </>
                )}
                {noPadding ? children : <div>{children}</div>}
            </CardContent>
        </Card>
    );
}

export default SectionCard;
export { CONTEXT as SECTION_CARD_CONTEXTS };
